import { QueryParameterBag } from '@smithy/types'

import {
  DroidMapMetadata,
  PaginationControls,
  ServiceClient,
  ServiceClientContext,
  queryParameterBagFromPaginationControls,
} from '@droidmap/shared-service-contract'

import {
  CreateTokenInput,
  CreateTokenResponse,
  GetTokenResponse,
  ListTokensInput,
  ListTokensResponse,
  RevokeTokenInput,
  RevokeTokenResponse,
  UpdateTokenInput,
  UpdateTokenResponse,
  VerifyTokenInput,
  VerifyTokenResponse,
} from '../models/token.js'

export class TokenServiceClient extends ServiceClient {
  public static readonly SERVICE_NAME = 'TokenService'

  constructor(input: ServiceClientContext) {
    super({ ...input, serviceName: TokenServiceClient.SERVICE_NAME })
  }

  /**
   * Create a new token
   */
  createToken = async (
    input: CreateTokenInput,
    metadata?: DroidMapMetadata,
  ): Promise<CreateTokenResponse> => {
    const response = await this.callApiRouteWithToken<
      CreateTokenInput,
      CreateTokenResponse
    >({
      method: 'POST',
      path: '/tokens',
      body: input,
      metadata,
    })

    return response
  }

  /**
   * List tokens with filters and pagination
   */
  listTokens = async (
    input: ListTokensInput & PaginationControls,
    metadata?: DroidMapMetadata,
  ): Promise<ListTokensResponse> => {
    const {
      projectId,
      tokenType,
      resourceScope,
      resourceId,
      status,
      next,
      prev,
      pageSize,
    } = input

    // Build pagination query params
    const queryParameters: QueryParameterBag =
      queryParameterBagFromPaginationControls({
        next,
        prev,
        pageSize,
      })

    // Add filter params
    queryParameters['projectId'] = projectId

    if (tokenType) {
      queryParameters['tokenType'] = tokenType
    }
    if (resourceScope) {
      queryParameters['resourceScope'] = resourceScope
    }
    if (resourceId) {
      queryParameters['resourceId'] = resourceId
    }
    if (status && status.length > 0) {
      queryParameters['status'] = status.join(',')
    }

    const response = await this.callApiRouteWithToken<
      undefined,
      ListTokensResponse
    >({
      method: 'GET',
      path: '/tokens',
      queryParameters,
      metadata,
    })

    return response
  }

  /**
   * Get a single token by ID
   */
  getTokenById = async (
    tokenId: string,
    metadata?: DroidMapMetadata,
  ): Promise<GetTokenResponse> => {
    const response = await this.callApiRouteWithToken<
      undefined,
      GetTokenResponse
    >({
      method: 'GET',
      path: `/tokens/${tokenId}`,
      metadata,
    })

    return response
  }

  /**
   * Update a token
   */
  updateToken = async (
    input: UpdateTokenInput,
    metadata?: DroidMapMetadata,
  ): Promise<UpdateTokenResponse> => {
    const { tokenId, ...body } = input
    const response = await this.callApiRouteWithToken<
      Omit<UpdateTokenInput, 'tokenId'>,
      UpdateTokenResponse
    >({
      method: 'PATCH',
      path: `/tokens/${tokenId}`,
      body,
      metadata,
    })

    return response
  }

  /**
   * Revoke a token
   */
  revokeToken = async (
    input: RevokeTokenInput,
    metadata?: DroidMapMetadata,
  ): Promise<RevokeTokenResponse> => {
    const response = await this.callApiRouteWithToken<
      undefined,
      RevokeTokenResponse
    >({
      method: 'POST',
      path: `/tokens/${input.tokenId}/revoke`,
      metadata,
    })

    return response
  }

  /**
   * Verify a Branca token (service-internal).
   *
   * Decodes the token, checks its expiry, revocation state and type, and
   * returns the decoded identity + scopes. Intended for service-to-service
   * use (e.g. the tile service resolving a `?token=` XYZ credential) — the
   * signing key and revocation state never leave the token service.
   */
  verifyToken = async (
    input: VerifyTokenInput,
    metadata?: DroidMapMetadata,
  ): Promise<VerifyTokenResponse> => {
    const response = await this.callApiRouteWithToken<
      VerifyTokenInput,
      VerifyTokenResponse
    >({
      method: 'POST',
      path: '/tokens/verify',
      body: input,
      metadata,
    })

    return response
  }
}
